import React, { useContext, useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Button, Alert, message, Tooltip, Input, Select } from "antd";
import {
  IdcardOutlined,
  GiftOutlined,
  ArrowRightOutlined,
  CreditCardOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthProvider";
import PageHeader from "../components/PageHeader";
import CardHeader from "../components/shared/CardHeader";
import CustomModal from "../components/CustomModal";
import userModel from "../lib/firebase/userModel";
import { userKeys } from "../lib/queryKeys";
import {
  reauthenticateWithCredential,
  updatePassword,
  EmailAuthProvider,
  getAuth,
} from "firebase/auth";
import { Spacer, StyledInput } from "../styles/CommonElements";
import { LAYOUT_CONSTANTS, device } from "../lib/breakpoints";
import { PRICING, isFreeTierUser } from "../lib/pricing";
import { isOpsChatOnlySubscriber } from "../helpers/accountProduct";
import SubscriptionModal from "../components/billing/SubscriptionModal";
import {
  buildPortfolioExportBundle,
  downloadJsonFile,
  defaultPortfolioExportFilename,
} from "../lib/exportPortfolioData";

// Phone number formatting utility
const formatPhoneNumber = (phone) => {
  if (!phone) return "-";
  // Remove all non-numeric characters
  const phoneNumber = phone.replace(/[^\d]/g, "");

  // Don't format if empty
  if (!phoneNumber) return "-";

  // Don't format if too short
  if (phoneNumber.length < 10) return phone;

  // Format as (XXX) XXX-XXXX
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 10)}`;
};

// Phone number input formatting utility
const formatPhoneInput = (value) => {
  // Remove all non-numeric characters
  const phoneNumber = value.replace(/[^\d]/g, "");

  // Don't format if empty
  if (!phoneNumber) return "";

  // Don't format if too short
  if (phoneNumber.length < 4) return phoneNumber;

  // Format as (XXX) XXX-XXXX
  if (phoneNumber.length <= 3) {
    return `(${phoneNumber}`;
  } else if (phoneNumber.length <= 6) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  } else {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
      3,
      6
    )}-${phoneNumber.slice(6, 10)}`;
  }
};

// Phone number input component
const PhoneInput = ({ value, onChange, ...props }) => {
  const handleChange = (e) => {
    const formatted = formatPhoneInput(e.target.value);
    onChange(formatted);
  };

  const handleKeyPress = (e) => {
    // Only allow numbers, backspace, delete, tab, escape, enter
    const allowedKeys = ["Backspace", "Delete", "Tab", "Escape", "Enter"];
    const isNumber = /[0-9]/.test(e.key);

    if (!isNumber && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <StyledInput
      {...props}
      value={value}
      onChange={handleChange}
      onKeyPress={handleKeyPress}
      maxLength={14} // (XXX) XXX-XXXX format
    />
  );
};

// Modal Elements
const ModalTitle = styled.h2`
  font-family: var(--font-primary);
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
  text-align: center;
`;

const ModalButton = styled(Button)`
  height: 44px;
  padding: 0 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;

  &.ant-btn-primary {
    background: var(--color-primary);
    border-color: var(--color-primary);

    &:hover:not(:disabled) {
      background: var(--color-primary-dark);
      border-color: var(--color-primary-dark);
    }
  }
`;

const LuxuryModalOverride = styled.div`
  padding: 2rem;
  text-align: center;

  p {
    color: var(--color-text);
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
`;

const PasswordInput = styled(Input.Password)`
  /* Outer wrapper */
  &.ant-input-affix-wrapper {
    width: 100%;
    height: 48px;
    margin-bottom: 1rem;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    padding: 0 12px;
    display: flex;
    align-items: center;
    background: #fff;

    &:hover,
    &:focus,
    &.ant-input-affix-wrapper-focused {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 3px rgba(44, 95, 45, 0.1);
    }
  }

  /* Inner input */
  .ant-input {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    height: 100%;
    font-family: var(--font-secondary);
    font-size: 1rem;
  }

  /* Eye icon wrapper */
  .ant-input-suffix {
    display: flex;
    align-items: center;
  }
`;

const { Option } = Form;
const { Option: SelectOption } = Select;

const PLAN_OPTIONS = [
  {
    label: "Starter",
    value: "starter",
    maxUnits: PRICING?.starter?.unitCap || 2,
  },
  { label: "Pro", value: "pro", maxUnits: PRICING?.pro?.unitCap || 15 },
  {
    label: "Business",
    value: "business",
    maxUnits: PRICING?.business?.unitCap || 50,
  },
  {
    label: "Growth",
    value: "growth",
    maxUnits: PRICING?.growth?.unitCap || 100,
  },
];

// Experience label mapping (supports legacy values)
const EXPERIENCE_LABELS = {
  beginner: "Beginner (0-1 years)",
  intermediate: "Intermediate (2-5 years)",
  advanced: "Advanced (6-10 years)",
  expert: "Expert (10+ years)",
  experienced: "Experienced", // legacy/value synonym
};

export default function Profile() {
  const { user, userInfo } = useContext(AuthContext);
  const location = useLocation();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalValues, setOriginalValues] = useState({});
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // Track form changes reliably (AntD does not re-run effects on field edits by default)
  const handleValuesChange = () => {
    const current = form.getFieldsValue(true);
    // Normalize arrays for comparison
    const norm = (v) => (Array.isArray(v) ? [...v].sort() : v);
    const allKeys = new Set([
      ...Object.keys(originalValues),
      ...Object.keys(current),
    ]);
    let equal = true;
    allKeys.forEach((key) => {
      const a = norm(originalValues[key]);
      const b = norm(current[key]);
      if (Array.isArray(a) || Array.isArray(b)) {
        if (JSON.stringify(a || []) !== JSON.stringify(b || [])) equal = false;
      } else if (a !== b) {
        equal = false;
      }
    });
    setHasChanges(!equal);
  };

  // Deep-link from subscription modal: /profile#subscription-billing
  useEffect(() => {
    if (location.hash !== "#subscription-billing") return;
    const t = window.setTimeout(() => {
      const el = document.getElementById("subscription-billing");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 150);
    return () => window.clearTimeout(t);
  }, [location.hash, location.pathname]);

  // Load user profile data
  useEffect(() => {
    if (userInfo) {
      const normalizedBilling =
        (userInfo.billingCycle || "").toLowerCase() === "month"
          ? "monthly"
          : userInfo.billingCycle || "";
      const formValues = {
        firstName: userInfo.firstName || "",
        lastName: userInfo.lastName || "",
        email: userInfo.email || user?.email || "",
        phone: userInfo.phone ? formatPhoneInput(userInfo.phone) : "",
        portfolioSize: userInfo.portfolioSize || "",
        propertyTypes: userInfo.propertyTypes || [],
        otherPropertyType: userInfo.otherPropertyType || "",
        /* Empty string prevents Ant Design Select from showing placeholder */
        experienceLevel: userInfo.experienceLevel || undefined,
        selectedPlan: userInfo.selectedPlan || "",
        billingCycle: normalizedBilling,
      };
      form.setFieldsValue(formValues);
      setOriginalValues(formValues);
    }
  }, [userInfo, user, form]);

  // Detect form changes (exclude selectedPlan as it's managed via subscription modal)
  useEffect(() => {
    const subscription = form.getFieldsValue();
    const hasFormChanges = Object.keys(subscription).some((key) => {
      // Skip subscription plan - it's managed via SubscriptionModal
      if (key === "selectedPlan") return false;

      const currentValue = form.getFieldValue(key);
      const originalValue = originalValues[key];

      if (Array.isArray(currentValue) && Array.isArray(originalValue)) {
        return (
          JSON.stringify(currentValue.sort()) !==
          JSON.stringify(originalValue.sort())
        );
      }

      return currentValue !== originalValue;
    });

    setHasChanges(hasFormChanges);
  }, [form, originalValues]);

  // On save, update the user profile in Firestore
  const handleSave = async (values) => {
    setLoading(true);
    try {
      console.log("[Profile] Starting save operation...");
      console.log("[Profile] Form values:", values);

      if (!user?.uid) {
        throw new Error("User not authenticated");
      }

      const updated = {
        ...values,
        billingCycle:
          (values.billingCycle || "").toLowerCase() === "month"
            ? "monthly"
            : values.billingCycle || "",
        propertyTypes: Array.isArray(values.propertyTypes)
          ? values.propertyTypes
          : [],
        username: user.uid, // Add username field that userModel.update expects
        // Don't save selectedPlan - it's managed via SubscriptionModal
        // Keep existing subscription value from userInfo if it exists
      };

      // Remove selectedPlan from updated data (managed via subscription modal)
      delete updated.selectedPlan;

      console.log("[Profile] Updated data to save:", updated);

      // Update user profile in Firestore
      await userModel.update({
        id: user.uid,
        data: updated,
      });

      // Invalidate the user query cache to refresh the data
      await queryClient.invalidateQueries({
        queryKey: userKeys.user(user.uid),
      });

      message.success("Profile updated successfully.");

      // Update the original values to reflect the saved state
      setOriginalValues(updated);
      setHasChanges(false);
    } catch (error) {
      console.error("[Profile] Save error:", error);
      message.error(error.message || "Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      message.error("Please fill in all fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      message.error("New passwords do not match.");
      return;
    }
    setPasswordLoading(true);
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        throw new Error("No authenticated user found.");
      }
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      setShowPasswordModal(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      message.success("Password changed successfully.");
    } catch (error) {
      message.error(error.message || "Failed to change password.");
    } finally {
      setPasswordLoading(false);
    }
  };

  // Top summary card info
  const summary = {
    name:
      userInfo?.firstName && userInfo?.lastName
        ? `${userInfo.firstName} ${userInfo.lastName}`
        : userInfo?.displayName || user?.displayName || "-",
    email: userInfo?.email || user?.email || "-",
    phone: userInfo?.phone ? formatPhoneNumber(userInfo.phone) : "-",
    experience: userInfo?.experienceLevel
      ? EXPERIENCE_LABELS[userInfo.experienceLevel] || userInfo.experienceLevel
      : "-",
  };

  const hasFreeMonths = (userInfo?.freeMonths ?? 0) > 0;
  /** Full LandlordOps (e.g. Suite Growth/Scale) referral program; not Free Gateway or paid chat_* OpsChat */
  const showLandlordReferralSection =
    userInfo &&
    !isFreeTierUser(userInfo) &&
    !isOpsChatOnlySubscriber(userInfo);

  const handleExportPortfolio = async () => {
    if (!user?.uid) {
      message.error("Sign in to export your data.");
      return;
    }
    setExportLoading(true);
    try {
      const data = await buildPortfolioExportBundle(user.uid);
      downloadJsonFile(data, defaultPortfolioExportFilename());
      const errs = data?.exportMeta?.partialErrors;
      if (errs?.length) {
        message.warning(
          "Download started. A few items couldn’t be included; the file includes a short note if you need the details."
        );
      } else {
        message.success("Your data download has started.");
      }
      setShowExportModal(false);
    } catch (err) {
      message.error(err?.message || "Could not export your data.");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Container className="profile-container">
      <PageHeader
        title="Profile"
        subtitle={`Review and edit your property management preferences and account information`}
        icon={IdcardOutlined}
      />
      {/* Show a warning if no client is found for this user */}
      {!userInfo && (
        <Alert
          message="Profile information not found."
          description="Please complete your profile setup to get the most out of LandlordOps."
          type="info"
          showIcon
          style={{ marginBottom: "2rem" }}
        />
      )}
      <SummaryCard style={{ marginBottom: "2rem" }}>
        <SummaryRow>
          <SummaryLabel>Full Name</SummaryLabel>
          <SummaryValue>{summary.name}</SummaryValue>
        </SummaryRow>
        <SummaryRow>
          <SummaryLabel>Contact Email</SummaryLabel>
          <SummaryValue>{summary.email}</SummaryValue>
        </SummaryRow>
        <SummaryRow>
          <SummaryLabel>Phone Number</SummaryLabel>
          <SummaryValue>{summary.phone}</SummaryValue>
        </SummaryRow>
        <SummaryRow>
          <SummaryLabel>Experience Level</SummaryLabel>
          <SummaryValue>{summary.experience}</SummaryValue>
        </SummaryRow>
        <ChangePasswordButton onClick={() => setShowPasswordModal(true)}>
          Change Password
        </ChangePasswordButton>
      </SummaryCard>

      {/* Free months / landlord referral: full OS (e.g. Suite) only; hidden on OpsChat */}
      {showLandlordReferralSection && (
        <FreeMonthsCard>
          <FreeMonthsContent>
            <FreeMonthsHeadline>
              {hasFreeMonths
                ? "Keep the savings rolling."
                : "Start earning FREE months."}
            </FreeMonthsHeadline>

            <FreeMonthsDescription>
              {hasFreeMonths
                ? `You still have ${userInfo.freeMonths} free ${
                    userInfo.freeMonths === 1 ? "month" : "months"
                  } of service remaining.
                 Invite another landlord to add more time instantly.`
                : `Refer other landlords and you’ll both get a FREE MONTH when they sign up and pay. Share your invite link and start saving!`}
            </FreeMonthsDescription>
          </FreeMonthsContent>

          <FreeMonthsCTA to="/offers">
            {hasFreeMonths ? "Share Invite" : "Get Invite Link"}
            <ArrowRightOutlined />
          </FreeMonthsCTA>
        </FreeMonthsCard>
      )}

      <ExportDataRow>
        <ExportDataRowText>
          We hope you stay on LandlordOps. If you need a one-time backup, use
          data export. Details are in the next step.
        </ExportDataRowText>
        <ExportDataRowCTA
          type="default"
          onClick={() => setShowExportModal(true)}
        >
          Data export
        </ExportDataRowCTA>
      </ExportDataRow>

      <CustomModal
        isOpen={showExportModal}
        onClose={() => !exportLoading && setShowExportModal(false)}
        showCloseIcon
        lightCloseForDarkHeader
        isCentered
        width={480}
        maskClosable={!exportLoading}
        closable={!exportLoading}
        destroyOnClose
        rootClassName="lo-profile-export-modal"
        styles={{
          content: { padding: 0, margin: 0, overflow: "hidden" },
          body: { padding: 0, margin: 0 },
        }}
      >
        <ExportModalOverride>
          <ExportModalHeader>
            <ExportModalTitle>Data export</ExportModalTitle>
          </ExportModalHeader>
          <ExportModalBody>
            <ExportModalIntro>
              LandlordOps is built to be your system of record. The download is
              a single <strong>JSON</strong> file (structured data for a full
              backup). Use it for personal backup, recordkeeping, or when
              something outside the app asks for it, not as an everyday feature.
            </ExportModalIntro>
            <ExportModalListLabel>
              May be included in the file
            </ExportModalListLabel>
            <ExportModalListPanel>
              <ExportModalList>
                <li>Profile and preferences</li>
                <li>Doors (units) and addresses</li>
                <li>Tenants and lease-related fields we have on file</li>
                <li>Vendors and contractors (including archived)</li>
                <li>Maintenance tickets</li>
                <li>Leases</li>
                <li>Expenses you entered in LandlordOps</li>
              </ExportModalList>
            </ExportModalListPanel>
            <ExportModalNote>
              Bank and card data for payments stays with Stripe and is not
              included. If part of your data can’t be included, you’ll still get
              a download, and a brief note in the file will call out anything
              that was skipped.
            </ExportModalNote>
            <ExportModalActions>
              <ExportModalCloseButton
                type="default"
                onClick={() => setShowExportModal(false)}
                disabled={exportLoading}
              >
                Close
              </ExportModalCloseButton>
              <ExportDownloadButton
                type="primary"
                icon={<DownloadOutlined />}
                loading={exportLoading}
                onClick={handleExportPortfolio}
              >
                {exportLoading ? "Preparing…" : "Download JSON"}
              </ExportDownloadButton>
            </ExportModalActions>
          </ExportModalBody>
        </ExportModalOverride>
      </CustomModal>

      <CardHeader
        title="Investor Profile"
        subtitle="Update your property management preferences and account information"
        style={{ marginTop: "0.5rem", marginBottom: "2.5rem" }}
      >
        <StyledForm
          form={form}
          layout="vertical"
          onFinish={handleSave}
          onValuesChange={handleValuesChange}
        >
          <Form.Item
            name="firstName"
            label={
              <span style={{ color: "var(--color-text)" }}>First Name</span>
            }
            rules={[
              { required: true, message: "Please enter your first name" },
            ]}
          >
            <StyledInput size="large" placeholder="Enter your first name" />
          </Form.Item>
          <Form.Item
            name="lastName"
            label={
              <span style={{ color: "var(--color-text)" }}>Last Name</span>
            }
            rules={[{ required: true, message: "Please enter your last name" }]}
          >
            <StyledInput size="large" placeholder="Enter your last name" />
          </Form.Item>
          <Form.Item
            name="email"
            label={
              <span style={{ color: "var(--color-text)" }}>Email Address</span>
            }
            rules={[
              { required: true, message: "Please enter your email address" },
              { type: "email", message: "Please enter a valid email address" },
            ]}
          >
            <StyledInput size="large" placeholder="Enter your email address" />
          </Form.Item>
          <Form.Item
            name="phone"
            label={
              <span style={{ color: "var(--color-text)" }}>Phone Number</span>
            }
            rules={[
              { required: true, message: "Please enter your phone number" },
            ]}
          >
            <PhoneInput size="large" placeholder="(555) 123-4567" />
          </Form.Item>
          <Form.Item
            name="portfolioSize"
            label={
              <span style={{ color: "var(--color-text)" }}>Portfolio Size</span>
            }
            rules={[
              { required: true, message: "Please select your portfolio size" },
            ]}
          >
            <Select size="large" placeholder="Select your portfolio size">
              <SelectOption value="1-2">1-2 units</SelectOption>
              <SelectOption value="3-5">3-5 units</SelectOption>
              <SelectOption value="6-10">6-10 units</SelectOption>
              <SelectOption value="11-25">11-25 units</SelectOption>
              <SelectOption value="26-50">26-50 units</SelectOption>
              <SelectOption value="51+">51+ units</SelectOption>
            </Select>
          </Form.Item>
          <Form.Item
            name="propertyTypes"
            className="property-types-select"
            label={
              <span style={{ color: "var(--color-text)" }}>Property Types</span>
            }
            rules={[
              {
                required: true,
                message: "Please select at least one property type",
              },
            ]}
            validateTrigger={["onChange", "onBlur"]}
          >
            <Select
              size="large"
              mode="multiple"
              placeholder="Select property types"
              dropdownStyle={{
                fontFamily: "var(--font-secondary)",
                fontSize: "1.15rem",
              }}
              className="property-types-select"
            >
              <SelectOption value="single-family">Single Family</SelectOption>
              <SelectOption value="multi-family">Multi Family</SelectOption>
              <SelectOption value="condo">Condo</SelectOption>
              <SelectOption value="townhouse">Townhouse</SelectOption>
              <SelectOption value="apartment">Apartment</SelectOption>
              <SelectOption value="commercial">Commercial</SelectOption>
              <SelectOption value="other">Other</SelectOption>
            </Select>
          </Form.Item>
          <Form.Item
            name="otherPropertyType"
            label={
              <span style={{ color: "var(--color-text)" }}>
                Other Property Type
              </span>
            }
          >
            <StyledInput
              size="large"
              placeholder="Specify other property type"
            />
          </Form.Item>
          <Form.Item
            name="experienceLevel"
            label={
              <span style={{ color: "var(--color-text)" }}>
                Experience Level
              </span>
            }
            rules={[
              {
                required: true,
                message: "Please select your experience level",
              },
            ]}
          >
            <Select size="large" placeholder="Select your experience level">
              <SelectOption value="experienced">Experienced</SelectOption>
              <SelectOption value="beginner">Beginner (0-1 years)</SelectOption>
              <SelectOption value="intermediate">
                Intermediate (2-5 years)
              </SelectOption>
              <SelectOption value="advanced">
                Advanced (6-10 years)
              </SelectOption>
              <SelectOption value="expert">Expert (10+ years)</SelectOption>
            </Select>
          </Form.Item>
          <SubscriptionAnchor id="subscription-billing">
            <Form.Item
              name="selectedPlan"
              label={
                <span style={{ color: "var(--color-text)" }}>
                  Subscription Plan
                </span>
              }
              help="Your current subscription plan determines available features and usage limits"
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "1rem" }}
              >
                <Input
                  size="large"
                  value={
                    PLAN_OPTIONS.find(
                      (opt) =>
                        opt.value ===
                        (form.getFieldValue("selectedPlan") ||
                          userInfo?.subscription ||
                          "starter")
                    )?.label || "Starter"
                  }
                  readOnly
                  style={{ flex: 1, cursor: "not-allowed" }}
                />
                <Button
                  type="primary"
                  size="large"
                  onClick={() => setShowSubscriptionModal(true)}
                  icon={
                    <CreditCardOutlined
                      style={{ filter: "brightness(0) invert(1)" }}
                    />
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "40px",
                    lineHeight: "1.5",
                  }}
                >
                  Manage
                </Button>
              </div>
            </Form.Item>
          </SubscriptionAnchor>
        </StyledForm>

        {/* Save button - always visible since we're always in editing mode */}
        <SaveButtonContainer>
          <SaveChangesButton
            type="primary"
            size="large"
            loading={loading}
            onClick={() => form.submit()}
            disabled={!hasChanges || loading}
          >
            {loading ? "Saving..." : "Save Changes"}
          </SaveChangesButton>
        </SaveButtonContainer>
      </CardHeader>

      <CustomModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        showCloseIcon
        isCentered
        width={420}
      >
        <LuxuryModalOverride>
          <ModalTitle>Change Password</ModalTitle>
          <Spacer $marginTop="1rem" />
          <PasswordInput
            placeholder="Current Password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
          />
          <PasswordInput
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
          />
          <PasswordInput
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
          />
          <ModalButton
            disabled={passwordLoading}
            onClick={handleChangePassword}
          >
            {passwordLoading ? "Changing..." : "Confirm"}
          </ModalButton>
        </LuxuryModalOverride>
      </CustomModal>

      {/* Subscription Management Modal */}
      <SubscriptionModal
        visible={showSubscriptionModal}
        onCancel={() => setShowSubscriptionModal(false)}
        onResume={() => setShowSubscriptionModal(true)}
        userInfo={userInfo}
        onUpdate={() => {
          // Refresh user data
          queryClient.invalidateQueries(userKeys.user());
        }}
      />
    </Container>
  );
}

const SubscriptionAnchor = styled.div`
  scroll-margin-top: 88px;
`;

const Container = styled.div`
  min-height: 100vh;
  background: var(--color-bg-primary);
  padding: 2rem;
  width: ${LAYOUT_CONSTANTS.DESKTOP_CONTENT_WIDTH};
  margin: 0 auto;

  @media ${device.mobile} {
    padding: 1rem;
    width: 95vw;
    min-width: ${LAYOUT_CONSTANTS.MIN_CONTENT_WIDTH};
  }

  @media (min-width: 768px) and (max-width: 1200px) {
    width: 90vw;
  }

  .property-types-select {
    .ant-select-selector {
      width: 100% !important;
    }
  }
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;

  @media ${device.mobile} {
    padding: 1.5rem;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }

  @media ${device.mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 1rem 0;
  }
`;

const SummaryLabel = styled.span`
  font-family: var(--font-secondary);
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text);

  @media ${device.mobile} {
    font-size: 0.9rem;
  }
`;

const SummaryValue = styled.span`
  font-family: var(--font-secondary);
  font-size: 1rem;
  color: var(--color-text-light);

  @media ${device.mobile} {
    font-size: 0.9rem;
    word-break: break-word;
  }
`;

const ChangePasswordButton = styled.button`
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-primary-dark);
    transform: translateY(-1px);
  }
`;

const ExportDataRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  margin-bottom: 2rem;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

  @media ${device.mobile} {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ExportDataRowText = styled.p`
  margin: 0;
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
  color: var(--color-text-light);
  flex: 1 1 220px;
  min-width: 0;

  @media ${device.mobile} {
    flex: 0 0 auto;
    width: 100%;
  }
`;

const ExportDataRowCTA = styled(Button)`
  && {
    height: 40px;
    padding: 0 1.1rem;
    font-weight: 600;
    font-family: var(--font-secondary);
    font-size: 0.9rem;
    border-radius: 8px;
    border: 1px solid var(--color-primary);
    color: var(--color-primary);
    background: #fff;
  }

  @media ${device.mobile} {
    && {
      width: 100%;
    }
  }

  &&:hover:not(:disabled),
  &&:hover:not(:disabled) * {
    color: #fff !important;
  }

  &&:hover:not(:disabled) {
    background: var(--color-primary) !important;
    border-color: var(--color-primary) !important;
  }
`;

const ExportModalOverride = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  text-align: left;
  border-radius: 10px;
  overflow: hidden;
  width: 100%;
  margin: 0;
  flex: 1;
`;

const ExportModalHeader = styled.div`
  background: var(
    --gradient-primary,
    linear-gradient(135deg, #2c5f2d 0%, #4a7c59 100%)
  );
  border-radius: 10px 10px 0 0;
  padding: 1.1rem 1.35rem 1rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(44, 95, 45, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
`;

const ExportModalTitle = styled.h2`
  font-family: var(--font-primary);
  font-size: 1.2rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.02em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const ExportModalBody = styled.div`
  padding: 1.35rem 1.4rem 1.25rem;
  background: #ffffff;
  border-radius: 0 0 10px 10px;
  @media ${device.mobile} {
    padding: 1.2rem 1.1rem 1.1rem;
  }
`;

const ExportModalIntro = styled.p`
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--color-text);
  margin: 0 0 1.1rem 0;
  text-align: left;
`;

const ExportModalListLabel = styled.p`
  font-family: var(--font-secondary);
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--color-primary-darker, #2c5f2d);
  margin: 0 0 0.5rem 0;
  text-align: left;
`;

const ExportModalListPanel = styled.div`
  padding: 0.75rem 0.9rem 0.85rem;
  margin: 0 0 1.1rem 0;
  background: linear-gradient(180deg, #f4faf4 0%, #ecf2ec 100%);
  border: 1px solid rgba(44, 95, 45, 0.14);
  border-radius: 10px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
`;

const ExportModalList = styled.ul`
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--color-text);
  margin: 0;
  padding: 0 0 0 1.1rem;
  list-style: disc;
  list-style-position: outside;
  text-align: left;

  li {
    font-size: 0.9rem !important;
    line-height: 1.6;
    margin-bottom: 0.32em;
  }

  li:last-child {
    margin-bottom: 0;
  }

  li::marker {
    color: var(--color-primary-darker, #2c5f2d);
  }
`;

const ExportModalNote = styled.p`
  font-family: var(--font-secondary);
  font-size: 0.8rem;
  line-height: 1.55;
  color: var(--color-text);
  margin: 0;
  padding: 0.75rem 0.9rem;
  background: #f6faf6;
  border: 1px solid rgba(44, 95, 45, 0.12);
  border-left: 3px solid var(--color-primary-darker, #2c5f2d);
  border-radius: 8px;
  text-align: left;
  opacity: 0.95;
`;

const ExportModalActions = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.4rem;
  padding-top: 1.1rem;
  border-top: 1px solid rgba(44, 95, 45, 0.1);

  @media ${device.mobile} {
    flex-direction: column;
    width: 100%;
    align-items: stretch;
    justify-content: stretch;
    margin-top: 1.25rem;
    padding-top: 0.85rem;
    & > * {
      width: 100% !important;
      max-width: 100%;
    }
  }
`;

const ExportModalCloseButton = styled(Button)`
  && {
    height: 44px;
    min-width: 100px;
    font-weight: 600;
    font-family: var(--font-secondary);
    font-size: 0.95rem;
    border-radius: 10px;
    border: 1px solid rgba(44, 95, 45, 0.4);
    background: #fff;
    color: var(--color-primary, #2c5f2d);
  }

  @media ${device.mobile} {
    && {
      width: 100%;
      min-width: 0;
    }
  }

  &&:hover:not(:disabled),
  &&:hover:not(:disabled) * {
    color: #fff !important;
  }

  &&:hover:not(:disabled) {
    border-color: var(--color-primary) !important;
    background: var(--color-primary) !important;
  }
`;

const ExportDownloadButton = styled(Button)`
  &&.ant-btn {
    height: 44px;
    min-width: 120px;
    font-weight: 600;
    font-family: var(--font-secondary);
    font-size: 0.95rem;
    border-radius: 10px;
    background: var(--color-primary-darker, #2c5f2d) !important;
    border-color: var(--color-primary-darker, #2c5f2d) !important;
    color: #fff !important;
  }
  @media ${device.mobile} {
    &&.ant-btn {
      width: 100%;
      min-width: 0;
    }
  }
  && .anticon {
    color: #fff !important;
  }
  &:hover:not(:disabled) {
    background: #244d25 !important;
    border-color: #244d25 !important;
    color: #fff !important;
  }
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 2rem;
  }

  .ant-form-item-label > label {
    font-family: var(--font-secondary) !important;
    font-size: 1rem !important;
    font-weight: 600 !important;
    color: var(--color-text) !important;
    margin-bottom: 0.5rem !important;
    display: block !important;
  }

  .ant-form-item-explain {
    font-family: var(--font-secondary) !important;
    font-size: 0.875rem !important;
    color: var(--color-text-light) !important;
    margin-top: 0.5rem !important;
    line-height: 1.4 !important;
  }

  .ant-select-selector {
    position: relative !important;
    display: flex !important;
    align-items: center !important;
    background-color: var(--color-bg-secondary) !important;
    border: 2px solid var(--color-border-medium) !important;
    border-radius: 12px !important;
    padding: 0.875rem 1rem !important;
    padding-right: 30px !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    min-height: 48px !important;
    height: auto !important;
  }

  /* Single select should have fixed height for proper arrow alignment */
  .ant-select-single .ant-select-selector {
    height: 48px !important;
  }

  .ant-select-selector:hover {
    border-color: var(--color-primary) !important;
  }

  .ant-select-selector:focus {
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 3px rgba(44, 95, 45, 0.1) !important;
  }

  .ant-select-selection-placeholder {
    color: var(--color-text-muted) !important;
    font-family: var(--font-secondary) !important;
  }

  .ant-select-selection-item {
    color: var(--color-text) !important;
    font-family: var(--font-secondary) !important;
  }

  /* Multi-select specific styling */
  .ant-select-multiple .ant-select-selector {
    min-height: 48px !important;
    height: auto !important;
    padding: 0.5rem 1rem !important;
    padding-right: 30px !important;
    display: flex !important;
    align-items: center !important;
  }

  /* For multi-select, arrow should be centered based on actual selector height */
  .ant-select-multiple .ant-select-arrow {
    top: 50% !important;
    transform: translateY(-50%) !important;
  }

  .ant-select-multiple .ant-select-selection-item {
    background: #ffffff !important;
    border: 1px solid var(--color-border-medium) !important;
    color: var(--color-text) !important;
    border-radius: 6px !important;
    margin: 0.25rem 0.25rem 0.25rem 0 !important;
    padding: 0.25rem 0.5rem !important;
    font-size: 0.875rem !important;
    line-height: 1.4 !important;
    max-width: 200px !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    display: inline-flex !important;
    align-items: center !important;
  }

  .ant-select-multiple .ant-select-selection-item-content {
    color: var(--color-text) !important;
    font-weight: 500 !important;
  }

  .ant-select-multiple .ant-select-selection-item-remove {
    color: var(--color-text) !important;
    margin-left: 0.5rem !important;
    display: flex !important;
    align-items: center !important;
  }

  .ant-select-multiple .ant-select-selection-placeholder {
    color: var(--color-text-muted) !important;
    font-family: var(--font-secondary) !important;
    line-height: 1.4 !important;
    padding: 0.5rem 0 !important;
  }

  .ant-select-multiple .ant-select-selection-search {
    margin: 0.25rem 0 !important;
  }

  .ant-select-multiple .ant-select-selection-search-input {
    height: auto !important;
    line-height: 1.4 !important;
  }

  /* Property types (multi-select) - ensure arrow sits inside the field */
  .property-types-select.ant-select-multiple,
  .property-types-select {
    position: relative !important;

    &.ant-select {
      position: relative !important;
      width: 100% !important;
      overflow: visible !important;
    }

    .ant-select-selector {
      padding-right: 50px !important;
      position: relative !important;
      box-sizing: border-box !important;
      overflow: hidden !important;
    }

    .ant-select-arrow,
    &.ant-select .ant-select-arrow {
      right: 20px !important;
      inset-inline-end: 20px !important;
      z-index: 10 !important;
      position: absolute !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
      pointer-events: none !important;
      margin: 0 !important;
    }

    /* Ensure the selection overflow doesn't push arrow out */
    .ant-select-selection-overflow {
      padding-right: 0 !important;
      margin-right: 0 !important;
      max-width: calc(100% - 50px) !important;
      overflow: hidden !important;
      box-sizing: border-box !important;
    }

    /* Prevent tags from overlapping arrow area */
    .ant-select-selection-item {
      margin-right: 4px !important;
    }

    /* Ensure selector content doesn't overflow */
    .ant-select-selection-search {
      max-width: calc(100% - 50px) !important;
    }
  }

  /* Fix vertical alignment of all Select dropdown arrows */
  /* Ensure .ant-select container matches selector height */
  .ant-select {
    position: relative !important;
    display: inline-flex !important;
    width: 100% !important;
  }

  /* Single select container should match selector height */
  .ant-select-single {
    height: 48px !important;
  }

  /* Multi-select container should match dynamic selector height */
  .ant-select-multiple {
    min-height: 48px !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    display: flex !important;
    align-items: center !important;
    line-height: 1.5 !important;
  }

  /* Override Ant Design's default arrow positioning */
  /* Arrow is positioned relative to .ant-select container, which should match selector height */
  .ant-select-arrow {
    position: absolute !important;
    top: 50% !important;
    right: 11px !important;
    transform: translateY(-50%) !important;
    margin: 0 !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    line-height: 1 !important;
    height: auto !important;
    width: auto !important;
    pointer-events: none !important;
    inset-inline-end: 11px !important;
    inset-block-start: auto !important;

    .anticon {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      line-height: 1 !important;
      margin: 0 !important;
      vertical-align: middle !important;
    }

    svg {
      display: inline-block !important;
      vertical-align: middle !important;
      margin: 0 !important;
    }
  }

  /* Ensure all variants are centered */
  .ant-select-single .ant-select-arrow,
  .ant-select-multiple .ant-select-arrow,
  .ant-select-large .ant-select-arrow,
  .ant-select-single.ant-select-large .ant-select-arrow,
  .ant-select-multiple.ant-select-large .ant-select-arrow {
    top: 50% !important;
    transform: translateY(-50%) !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  #selectedPlan_help {
    margin-top: 1.5rem !important;
  }
`;

const SaveButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #f1f5f9;
`;

const SaveChangesButton = styled(Button)`
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
  height: 48px;
  padding: 0 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;

  &:hover:not(:disabled) {
    background: var(--color-primary-dark);
    border-color: var(--color-primary-dark);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Free Months Section Styled Components
const FreeMonthsCard = styled.div`
  border-radius: 24px;
  padding: 1.75rem 2rem;
  margin-bottom: 1.5rem;
  border: 2px solid rgba(249, 181, 64, 0.6);
  background: linear-gradient(115deg, #fff5db 0%, #ffe7b0 55%, #ffd98c 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  flex-wrap: wrap;
  box-shadow: 0 15px 35px rgba(222, 159, 59, 0.25),
    0 0 0 1px rgba(249, 181, 64, 0.2), 0 0 20px rgba(249, 181, 64, 0.15);
`;

const FreeMonthsContent = styled.div`
  flex: 1;
  min-width: 260px;
  text-align: left;
`;

const FreeMonthsHeadline = styled.h3`
  font-family: var(--font-secondary);
  font-size: 1.5rem;
  font-weight: 800;
  color: #1f1b10;
  margin: 0 0 0.5rem 0;
  text-align: left;
`;

const FreeMonthsDescription = styled.p`
  font-family: var(--font-secondary);
  font-size: 1rem;
  color: #3f2a08;
  line-height: 1.6;
  margin: 0;
  max-width: 520px;
  text-align: left;
`;

const FreeMonthsCTA = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #1f391f;
  color: #ffffff;
  font-weight: 700;
  padding: 0.75rem 1.35rem;
  border-radius: 999px;
  text-decoration: none;
  border: none;
  min-width: 165px;
  justify-content: center;
  box-shadow: 0 8px 18px rgba(31, 57, 31, 0.3);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  .anticon {
    color: #ffffff !important;
    filter: brightness(0) invert(1) !important;
    font-size: 1.1rem;
    opacity: 1 !important;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 22px rgba(31, 57, 31, 0.35);
  }
`;
